import { Card, Tag, Typography, Row, Col, Space, Spin, Empty, Input, Select } from "antd";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { RegistryDisplayOrganizationName } from "./RegistryDisplayOrgName";
import { useGetRegistryQuery } from "./store/registryapi";
import TruncateText from "./TruncateText";
import moment from "moment";

const { Search } = Input;
const { Option } = Select;

interface ProjectRecord {
  id?: string;
  project_id: string;
  name: string;
  type: string;
  project_period: [string, string];
  organization: string;
  status: string;
  region: string;
  [key: string]: any;
}

function RegistryLandingPageView() {
  const { data, isLoading, isError } = useGetRegistryQuery(undefined);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const history = useHistory();

  const handleViewDetails = (record: ProjectRecord) => {
    const projectId = record?.project_id;
    history.push({
      pathname: `/${projectId}`,
    });
  };

  const formatProjectPeriod = (period: [string, string]) => {
    if (!period || period.length < 2) return "-";

    try {
      const startDate = moment(period[0]);
      const endDate = moment(period[1]);

      return `${startDate.format("MMM D, YYYY")} - ${endDate.format(
        "MMM D, YYYY"
      )}`;
    } catch (e) {
      return "Invalid date";
    }
  };

  // Get unique statuses for filter
  const uniqueStatuses = Array.from(new Set(data?.data?.map((item: ProjectRecord) => item.status) || []));

  // Filter data based on search and status
  const filteredData = data?.data?.filter((record: ProjectRecord) => {
    const searchLower = searchText.toLowerCase();
    const matchesSearch = (
      record.name?.toLowerCase().includes(searchLower) ||
      record.project_id?.toLowerCase().includes(searchLower) ||
      record.type?.toLowerCase().includes(searchLower) ||
      record.region?.toLowerCase().includes(searchLower)
    );
    const matchesStatus = !statusFilter || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  return (
    <div style={{ minHeight: "80vh" }}>
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Dockal Registry
          </Typography.Title>
          <Typography.Text type="secondary">
            {filteredData.length} project{filteredData.length !== 1 ? 's' : ''} found
          </Typography.Text>
        </div>
      </div>

      {/* Description Section */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Paragraph style={{ fontSize: 16 }}>
          Access a complete overview of all carbon credits in the Dockal Registry. Track credit status,
          issuance history, and retirement records across all certified projects.
        </Typography.Paragraph>
      </div>

      {/* Search and Filter Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={16}>
          <Search
            placeholder="Search by project name, ID, type, or region..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            enterButton
          />
        </Col>
        <Col xs={24} md={8}>
          <Select
            placeholder="Filter by status"
            allowClear
            style={{ width: "100%" }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
          >
            {uniqueStatuses.map((status: string) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Cards Section */}
      <Spin spinning={isLoading}>
        {isError ? (
          <Card style={{ borderRadius: 8 }}>
            <Empty description="Error fetching data" />
          </Card>
        ) : filteredData.length === 0 ? (
          <Card style={{ borderRadius: 8 }}>
            <Empty
              description={
                searchText
                  ? "No projects match your search criteria"
                  : "No projects found"
              }
            />
          </Card>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {filteredData.map((record: ProjectRecord) => (
              <Card
                key={record.project_id}
                hoverable
                style={{
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  border: '1px solid #f0f0f0',
                }}
                onClick={() => handleViewDetails(record)}
                bodyStyle={{ padding: '20px 24px' }}
              >
                <Row gutter={16} align="middle" wrap={false}>
                  {/* Project ID */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Project ID
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        <TruncateText text={record.project_id} length={15} />
                      </Typography.Text>
                    </div>
                  </Col>

                  {/* Name */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Name
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        <TruncateText text={record.name} length={20} />
                      </Typography.Text>
                    </div>
                  </Col>

                  {/* Type */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Type
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13, textTransform: 'capitalize' }}>
                        {record.type}
                      </Typography.Text>
                    </div>
                  </Col>

                  {/* Organization */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Organization
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        <RegistryDisplayOrganizationName orgid={record.organization} place="table" />
                      </Typography.Text>
                    </div>
                  </Col>

                  {/* Project Period */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Project Period
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        {formatProjectPeriod(record.project_period)}
                      </Typography.Text>
                    </div>
                  </Col>

                  {/* Status */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Status
                      </Typography.Text>
                      <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                        {record.status}
                      </Tag>
                    </div>
                  </Col>

                  {/* Region */}
                  <Col flex="1">
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                        Region
                      </Typography.Text>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        <TruncateText text={record.region} />
                      </Typography.Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        )}
      </Spin>
    </div>
  );
}

export default RegistryLandingPageView;
